/**
 * Print choice for the work-order PDF (#4107): labour steps summarised as one
 * row per job (the default, as on every customer document since #4114) or each
 * step on its own row.
 *
 * The print window is opened here, inside the click, before the PDF is
 * downloaded, so the popup blocker still counts it as the user's gesture
 * (#4093). A failed download closes that window again and says so.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { useNotify } from '@/hooks/useNotify';
import { printWorkOrderPdf, type WorkOrderPdfLabourDetail } from '@/hooks/useWorkOrders';

export interface WorkOrderPrintDialogProps {
  open: boolean;
  onClose: () => void;
  workOrderId: string;
}

export function WorkOrderPrintDialog({ open, onClose, workOrderId }: WorkOrderPrintDialogProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const [labourDetail, setLabourDetail] = useState<WorkOrderPdfLabourDetail>('collapsed');
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    if (open) setLabourDetail('collapsed');
  }, [open]);

  const handlePrint = (): void => {
    // Synchronous, before any await — see the note above.
    const popup = window.open('', '_blank');
    setPrinting(true);
    printWorkOrderPdf(workOrderId, { labourDetail }, popup)
      .then(() => {
        onClose();
      })
      .catch(() => {
        notify.error(tc('error'), t('print.popupBlocked'));
      })
      .finally(() => {
        setPrinting(false);
      });
  };

  return (
    <ResponsiveDialog
      open={open}
      onClose={onClose}
      mobileTitle={t('printDialog.title')}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>{t('printDialog.title')}</DialogTitle>
      <DialogContent data-testid="work-order-print-dialog">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('printDialog.description')}
        </Typography>
        <FormControl>
          <FormLabel id="work-order-print-labour-detail">{t('printDialog.labourDetailLabel')}</FormLabel>
          <RadioGroup
            aria-labelledby="work-order-print-labour-detail"
            value={labourDetail}
            onChange={(event) => {
              setLabourDetail(event.target.value === 'itemized' ? 'itemized' : 'collapsed');
            }}
          >
            <FormControlLabel
              value="collapsed"
              control={<Radio data-testid="work-order-print-collapsed" />}
              label={t('printDialog.collapsed')}
            />
            <FormControlLabel
              value="itemized"
              control={<Radio data-testid="work-order-print-itemized" />}
              label={t('printDialog.itemized')}
            />
          </RadioGroup>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={printing} data-testid="work-order-print-cancel">
          {tc('cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handlePrint}
          disabled={printing}
          data-testid="work-order-print-confirm"
        >
          {t('printDialog.confirm')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
